import { useState } from 'react'
import {
  Clock,
  User,
  AlertTriangle,
  Flame,
  CheckCircle2,
  FolderOpen,
  ArrowRight,
  MoreVertical,
  CheckSquare,
  MessageSquare,
  Sparkles,
} from 'lucide-react'
import { Badge } from '../atoms'
import { cn } from '../atoms'

interface CasesKanbanBoardProps {
  cases: any[]
  onStatusChange: (caseId: string, newStatusName: string) => void
  onCaseClick: (caseId: string) => void
  isLoading?: boolean
}

interface KanbanColumn {
  id: string
  name: string
  statusNames: string[]
  color: string
  headerBg: string
  accentColor: string
}

const COLUMNS: KanbanColumn[] = [
  {
    id: 'new',
    name: 'Nuevos',
    statusNames: ['new'],
    color: 'border-blue-200 dark:border-blue-900',
    headerBg: 'bg-blue-50/80 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300',
    accentColor: 'bg-blue-500',
  },
  {
    id: 'in_progress',
    name: 'En Gestión',
    statusNames: ['in_progress', 'open', 'assigned'],
    color: 'border-amber-200 dark:border-amber-900',
    headerBg: 'bg-amber-50/80 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
    accentColor: 'bg-amber-500',
  },
  {
    id: 'pending',
    name: 'En Espera / Escalados',
    statusNames: ['pending', 'escalated', 'waiting'],
    color: 'border-purple-200 dark:border-purple-900',
    headerBg: 'bg-purple-50/80 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300',
    accentColor: 'bg-purple-500',
  },
  {
    id: 'closed',
    name: 'Resueltos / Cerrados',
    statusNames: ['closed', 'resolved'],
    color: 'border-emerald-200 dark:border-emerald-900',
    headerBg: 'bg-emerald-50/80 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300',
    accentColor: 'bg-emerald-500',
  },
]

const criticidadConfig: Record<string, { label: string; className: string }> = {
  CM: { label: 'Crítico Máximo', className: 'bg-red-600 text-white' },
  CRITICO_MAXIMO: { label: 'Crítico Máximo', className: 'bg-red-600 text-white' },
  C: { label: 'Crítico', className: 'bg-red-100 text-red-700 border border-red-300' },
  CRITICO: { label: 'Crítico', className: 'bg-red-100 text-red-700 border border-red-300' },
  critical: { label: 'Crítico', className: 'bg-red-100 text-red-700 border border-red-300' },
  A: { label: 'Alto', className: 'bg-amber-100 text-amber-700 border border-amber-300' },
  ALTO: { label: 'Alto', className: 'bg-amber-100 text-amber-700 border border-amber-300' },
  high: { label: 'Alto', className: 'bg-amber-100 text-amber-700 border border-amber-300' },
  M: { label: 'Medio', className: 'bg-blue-100 text-blue-700 border border-blue-300' },
  MEDIO: { label: 'Medio', className: 'bg-blue-100 text-blue-700 border border-blue-300' },
  medium: { label: 'Medio', className: 'bg-blue-100 text-blue-700 border border-blue-300' },
  B: { label: 'Bajo', className: 'bg-green-100 text-green-700 border border-green-300' },
  BAJO: { label: 'Bajo', className: 'bg-green-100 text-green-700 border border-green-300' },
  low: { label: 'Bajo', className: 'bg-green-100 text-green-700 border border-green-300' },
  INF: { label: 'Informativo', className: 'bg-gray-100 text-gray-600 border border-gray-300' },
  INFORMATIVO: { label: 'Informativo', className: 'bg-gray-100 text-gray-600 border border-gray-300' },
}

function getCardCriticality(item: any) {
  const raw = item.criticidad || item.emails?.[0]?.criticidad || item.riskLevel?.name || ''
  const key = String(raw).trim()
  return criticidadConfig[key] || criticidadConfig[key.toUpperCase()] || {
    label: key || 'Bajo',
    className: 'bg-slate-100 text-slate-700 border border-slate-300',
  }
}

function getSlaBadge(date: string | undefined): { color: string; label: string } | null {
  if (!date) return null
  const now = new Date().getTime()
  const d = new Date(date).getTime()
  if (isNaN(d)) return null
  if (d < now) return { color: 'bg-rose-100 text-rose-700 border-rose-300', label: 'SLA Vencido' }
  if (d < now + 4 * 60 * 60 * 1000) return { color: 'bg-amber-100 text-amber-700 border-amber-300', label: 'SLA por vencer' }
  return { color: 'bg-emerald-100 text-emerald-700 border-emerald-300', label: 'SLA en tiempo' }
}

export function CasesKanbanBoard({
  cases,
  onStatusChange,
  onCaseClick,
  isLoading = false,
}: CasesKanbanBoardProps) {
  const [draggedCaseId, setDraggedCaseId] = useState<string | null>(null)
  const [dragOverColId, setDragOverColId] = useState<string | null>(null)

  const handleDragStart = (e: React.DragEvent, caseId: string) => {
    e.dataTransfer.setData('text/plain', caseId)
    setDraggedCaseId(caseId)
  }

  const handleDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault()
    setDragOverColId(colId)
  }

  const handleDragLeave = () => {
    setDragOverColId(null)
  }

  const handleDrop = (e: React.DragEvent, targetCol: KanbanColumn) => {
    e.preventDefault()
    setDragOverColId(null)
    const caseId = e.dataTransfer.getData('text/plain') || draggedCaseId
    if (!caseId) return
    const targetStatus = targetCol.statusNames[0]
    onStatusChange(caseId, targetStatus)
    setDraggedCaseId(null)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start pb-6">
      {COLUMNS.map((col) => {
        const columnCases = cases.filter((c) => {
          const statusName = c.status?.name?.toLowerCase() || ''
          return col.statusNames.includes(statusName)
        })

        const isOver = dragOverColId === col.id

        return (
          <div
            key={col.id}
            onDragOver={(e) => handleDragOver(e, col.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col)}
            className={cn(
              'rounded-xl border bg-slate-50/70 dark:bg-gray-900/60 transition-all flex flex-col max-h-[calc(100vh-250px)] min-h-[500px]',
              col.color,
              isOver ? 'ring-2 ring-primary-500 bg-primary-50/20' : ''
            )}
          >
            {/* Column Header */}
            <div
              className={cn(
                'px-4 py-3 rounded-t-xl border-b flex items-center justify-between font-semibold text-xs',
                col.headerBg
              )}
            >
              <div className="flex items-center gap-2">
                <span className={cn('w-2 h-2 rounded-full', col.accentColor)} />
                <span>{col.name}</span>
              </div>
              <span className="px-2 py-0.5 rounded-full bg-white/70 dark:bg-gray-800/80 text-slate-800 dark:text-slate-200 text-2xs font-bold border border-slate-200 dark:border-gray-700">
                {columnCases.length}
              </span>
            </div>

            {/* Column Cards */}
            <div className="p-2.5 space-y-2.5 overflow-y-auto flex-1">
              {columnCases.length === 0 ? (
                <div className="text-center py-10 px-4 border-2 border-dashed border-slate-200 dark:border-gray-800 rounded-lg">
                  <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
                    Sin casos en esta columna
                  </p>
                </div>
              ) : (
                columnCases.map((caseItem) => {
                  const crit = getCardCriticality(caseItem)
                  const sla = getSlaBadge(caseItem.slaResponseDue || caseItem.dueDate)
                  const commCount = caseItem._count?.communications || caseItem.communications?.length || 0
                  const isHotThread = commCount >= 3
                  const tasks = Array.isArray(caseItem.tasks) ? caseItem.tasks : []
                  const completedTasks = tasks.filter((t: any) => t.isCompleted).length
                  const hasTasks = tasks.length > 0

                  return (
                    <div
                      key={caseItem.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, caseItem.id)}
                      onClick={() => onCaseClick(caseItem.id)}
                      className={cn(
                        'group bg-white dark:bg-gray-800 p-3.5 rounded-lg border border-slate-200 dark:border-gray-700 shadow-2xs hover:shadow-md hover:border-primary-400 transition-all cursor-pointer relative',
                        isHotThread ? 'ring-1 ring-amber-400/50' : ''
                      )}
                    >
                      {/* Top Badges */}
                      <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
                        <span className="text-2xs font-bold font-mono text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/60 px-1.5 py-0.5 rounded border border-primary-200 dark:border-primary-800">
                          {caseItem.caseNumber}
                        </span>

                        <span
                          className={cn(
                            'text-2xs px-2 py-0.5 rounded-full font-semibold',
                            crit.className
                          )}
                        >
                          {crit.label}
                        </span>
                      </div>

                      {/* Subject */}
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {caseItem.subject || '(Sin asunto)'}
                      </h4>

                      {/* Area & Mailbox Tag */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-2 text-2xs text-slate-500 dark:text-slate-400">
                        {caseItem.area && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-700 font-medium text-slate-700 dark:text-slate-300">
                            {caseItem.area.name}
                          </span>
                        )}
                        {caseItem.mailbox?.email && (
                          <span className="text-slate-400 dark:text-slate-500 truncate max-w-[140px]">
                            {caseItem.mailbox.email.split('@')[0]}
                          </span>
                        )}
                      </div>

                      {/* Indicators Bar (Hot Thread, Tasks, SLA) */}
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-2.5 border-t border-slate-100 dark:border-gray-700/60 text-2xs">
                        {isHotThread && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 font-bold border border-amber-200 dark:border-amber-800">
                            <Flame className="w-3 h-3 text-amber-600 animate-bounce" />
                            {commCount} msgs
                          </span>
                        )}

                        {hasTasks && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium border',
                              completedTasks === tasks.length
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-gray-700 dark:text-slate-300'
                            )}
                          >
                            <CheckSquare className="w-3 h-3" />
                            {completedTasks}/{tasks.length}
                          </span>
                        )}

                        {sla && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium border',
                              sla.color
                            )}
                          >
                            <Clock className="w-3 h-3" />
                            {sla.label}
                          </span>
                        )}
                      </div>

                      {/* Footer: Assignee & Quick Move */}
                      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-50 dark:border-gray-800 text-xs">
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                          <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-gray-700 flex items-center justify-center text-2xs font-bold text-slate-700 dark:text-slate-200">
                            {caseItem.assignee
                              ? `${caseItem.assignee.firstName?.[0] || 'U'}`
                              : '?'}
                          </div>
                          <span className="text-2xs truncate max-w-[90px]">
                            {caseItem.assignee
                              ? `${caseItem.assignee.firstName} ${caseItem.assignee.lastName || ''}`.trim()
                              : 'Sin asignar'}
                          </span>
                        </div>

                        {/* Quick Status Shift Dropdown */}
                        <select
                          value={col.id}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation()
                            const targetCol = COLUMNS.find((c) => c.id === e.target.value)
                            if (targetCol) {
                              onStatusChange(caseItem.id, targetCol.statusNames[0])
                            }
                          }}
                          className="text-2xs bg-slate-100 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-slate-700 dark:text-slate-200 font-medium hover:border-primary-400 focus:outline-none"
                        >
                          {COLUMNS.map((c) => (
                            <option key={c.id} value={c.id}>
                              ➔ {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
